import { useEffect } from 'react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router'
import * as Sentry from '@sentry/react'

import config from 'env'

const { sentry, env } = config

interface HintException {
  name?: string
  message?: string
  fileName?: string
}

if (sentry) {
  Sentry.init({
    dsn: 'https://7c4f289146c642ea8cdcae4ddf4e1587@o326695.ingest.sentry.io/6066978',
    beforeSend: (event, hint) => {
      /**
       * Below code adapted from https://gist.github.com/patrick-fs/8066c2a0c97aec6cca6d355a55a52506
       * Patrick from Fullstory provided an implementation of FullStory <-> sentry integration without packages
       */
      if (!event) return null
      const error = hint.originalException as HintException
      event.extra = event.extra || {}

      // @ts-expect-error
      const _fs = window[window._fs_namespace]

      if (typeof _fs !== 'function') {
        event.extra.fullstory = 'FullStory is not installed'
        return event
      }

      // getCurrentSessionURL isn't available until after the FullStory script is fully bootstrapped.
      // If an error occurs before getCurrentSessionURL is ready, make a note in Sentry and move on.
      // More on getCurrentSessionURL here: https://help.fullstory.com/develop-js/getcurrentsessionurl
      /** Add full story session URL to sentry event */
      event.extra.fullstory =
        typeof _fs.getCurrentSessionURL === 'function'
          ? _fs.getCurrentSessionURL(true)
          : 'current session URL API not ready'

      //   FS.event is immediately ready even if FullStory isn't fully bootstrapped
      /** Send event to FullStory */
      _fs.event('Application error: ', {
        message: error.message || 'unknown message',
        sentryEventId: hint.event_id,
        sentryUrl: `https://sentry.io/organizations/marshmallow-insurance/issues/?project=6066978&query=${hint.event_id}`,
      })

      return event
    },
    environment: env,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
    ],
    normalizeDepth: 10, // How deep you want your state context to be.
    ignoreErrors: ['Non-Error promise rejection captured'],
  })
}
